<template>
  <div class="membership--summary-container" v-if="user && user.membership && !loading">
    <v-card class="membership--summary" :style="membershipStyle">
      <!-- High-End Texture & Glass Shine Overlay -->
      <div class="glass--shine"></div>

      <!-- Watermark Background Branding -->
      <div class="card--watermark" v-if="membershipType">{{ membershipType }}</div>

      <!-- Centered Header: Name + Logo -->
      <div class="membership--info">
        <div class="membership--name">{{ membershipType }}</div>
        <img class="membership--logo" :src="user.membership.logo" v-if="user.membership.logo" />
      </div>

      <!-- Centered Glass Expiration Badge -->
      <div class="membership--expired">
        <v-icon small class="mr-1">mdi-calendar-clock-outline</v-icon>
        <span>{{ `${$t('general.endAt')} ` }}</span>
        <span>{{ $date.format(user.membership.expired_at, 'dd MMM yyyy') }}</span>
      </div>

      <!-- Centered Modern Glass CTA Pill -->
      <div class="membership--cta" v-if="membershipAction">
        <router-link
          class="cta--glass-btn"
          to="/upgrade"
          v-if="membershipAction.action === 'upgrade'"
        >
          <span>{{ $t('product.upgrade') }}</span>
          <v-icon small class="btn--arrow">mdi-arrow-right</v-icon>
        </router-link>
        <router-link
          class="cta--glass-btn"
          to="/product"
          v-else-if="membershipAction.action === 'new'"
        >
          <span>{{ $t('general.buyMembershipNow') }}</span>
          <v-icon small class="btn--arrow">mdi-arrow-right</v-icon>
        </router-link>
        <router-link
          class="cta--glass-btn"
          to="/renewal"
          v-else-if="membershipAction.action === 'renewal'"
        >
          <span>{{ $t('product.renewal.title') }}</span>
          <v-icon small class="btn--arrow">mdi-arrow-right</v-icon>
        </router-link>
      </div>

      <!-- Renewal Promo Block -->
      <div v-if="membershipAction && membershipAction.action === 'renewal'">
        <div class="membership--renewal" v-if="membershipAction.renewal_type === 'early_bird'">
          <div>
            {{ $t('product.renewal.earlyBirdDesc1') }} <b>{{ membershipAction.renewal_due_date }}</b
            >. <br />{{ $t('product.renewal.earlyBirdDesc2') }}
          </div>
          <div>
            <button
              type="button"
              class="btn btn--whatsapp"
              style="margin-left: auto; margin-right: auto; margin-top: 8px"
              @click="handleContact"
            >
              <img
                src="@/assets/img/icons/whatsapp-1.svg"
                alt="Phone"
                style="margin-right: 4px; width: 22px"
              />
              <span style="font-family: Poppins-SemiBold">{{ $t('general.contactUs') }}</span>
            </button>
          </div>
        </div>
        <div class="membership--renewal" v-else-if="membershipAction.renewal_type === 'regular'">
          {{ $t('product.renewal.reminderDesc') }}
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  components: {},
  props: [],
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.global.user,
      totalCoins: (state) => state.coins.totalCoins,
      membershipAction: (state) => state.global.membershipAction,
    }),
    ...mapGetters({
      membershipType: 'global/membershipType',
    }),
    membershipPalette() {
      const palettes = {
        PRO: {
          background: 'linear-gradient(135deg, #F5C04D 0%, #E5A422 100%)',
          color: '#000000',
          shadow: '0 8px 24px rgba(245, 192, 77, 0.34)',
        },
        EXECUTIVE: {
          background: 'linear-gradient(135deg, #3B3A38 0%, #201F1E 100%)',
          color: '#FFFFFF',
          shadow: '0 8px 24px rgba(32, 31, 30, 0.38)',
        },
        STARTER: {
          background: 'linear-gradient(135deg, #5B5C5C 0%, #3A3B3B 100%)',
          color: '#FFFFFF',
          shadow: '0 8px 24px rgba(91, 92, 92, 0.32)',
        },
        LITE: {
          background: 'linear-gradient(135deg, #F92E0B 0%, #BE1C02 100%)',
          color: '#FFFFFF',
          shadow: '0 8px 24px rgba(249, 46, 11, 0.35)',
        },
      };

      return palettes[this.membershipType] || palettes.LITE;
    },
    membershipStyle() {
      return {
        background: this.membershipPalette.background,
        color: this.membershipPalette.color,
        boxShadow: this.membershipPalette.shadow,
      };
    },
  },
  async mounted() {
    try {
      this.loading = true;
      await Promise.all([
        this.$store.dispatch('coins/getTotalCoins'),
        this.$store.dispatch('listingAvailability/getListingAvailability'),
        this.$store.dispatch('global/getMembershipAction'),
      ]);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    handleContact() {
      let url = '';
      const contactNumber = '+' + this.$t('general.phone2');
      const text =
        'Halo, saya butuh informasi lebih lanjut mengenai promo perpanjangan early bird.';
      url = `https://wa.me/${contactNumber}?text=${text}`;

      window.open(url, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/utils/variables';

.membership--summary-container {
  position: relative;
  @media #{$phones} {
    width: 100% !important;
  }
}

.membership--summary {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 32px;
  border-radius: 28px !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
  border-top: 1px solid rgba(255, 255, 255, 0.45) !important;
  isolation: isolate;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media #{$phones} {
    padding: 20px 24px;
    border-radius: 22px !important;
  }

  .glass--shine {
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.04) 0,
        rgba(255, 255, 255, 0.04) 1px,
        transparent 0,
        transparent 10px
      ),
      repeating-linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.04) 0,
        rgba(255, 255, 255, 0.04) 1px,
        transparent 0,
        transparent 10px
      ),
      radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 70%),
      linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.16) 0%,
        rgba(255, 255, 255, 0) 50%,
        rgba(0, 0, 0, 0.08) 100%
      );
    pointer-events: none;
  }

  .card--watermark {
    position: absolute;
    bottom: -16px;
    right: -10px;
    font-size: 3.8rem;
    font-family: Poppins-Bold, sans-serif;
    color: currentColor;
    opacity: 0.05;
    letter-spacing: 2px;
    pointer-events: none;
    user-select: none;
    white-space: nowrap;
    line-height: 1;
    z-index: -1;
  }

  .membership--info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-bottom: 8px;
    @media #{$phones} {
      margin-bottom: 6px;
    }
  }

  .membership--name {
    font-size: 1.85rem;
    font-family: Poppins-Bold, sans-serif;
    color: currentColor;
    letter-spacing: 1.2px;
    line-height: 1.1;

    @media #{$phones} {
      font-size: 1.45rem;
      letter-spacing: 1px;
    }
  }

  .membership--logo {
    width: 26px;
    height: 26px;
    object-fit: contain;
    margin-left: 6px;
  }

  .membership--expired {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.28);
    padding: 5px 16px;
    border-radius: 20px;
    margin-bottom: 14px;
    font-size: $font-sm;
    font-family: Poppins-Medium, sans-serif;
    color: currentColor;
    opacity: 0.95;
    text-align: center;
    backdrop-filter: blur(8px);
    box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.2);

    .v-icon {
      color: inherit !important;
      font-size: 15px !important;
      opacity: 0.9;
    }

    @media #{$phones} {
      font-size: $font-xs;
      padding: 4px 12px;
      margin-bottom: 10px;
    }
  }

  .membership--cta {
    width: 100%;
    display: flex;
    justify-content: center;

    .cta--glass-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: rgba(255, 255, 255, 0.22);
      border: 1px solid rgba(255, 255, 255, 0.38);
      color: currentColor !important;
      font-family: Poppins-SemiBold, sans-serif;
      font-size: 13px;
      padding: 8px 22px;
      border-radius: 20px;
      text-decoration: none !important;
      backdrop-filter: blur(8px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 1px rgba(255, 255, 255, 0.3);
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

      .btn--arrow {
        color: inherit !important;
        font-size: 15px !important;
        transition: transform 0.2s ease;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.35);
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14), inset 0 1px 2px rgba(255, 255, 255, 0.4);

        .btn--arrow {
          transform: translateX(3px);
        }
      }

      @media #{$phones} {
        font-size: 12px;
        padding: 6px 16px;
      }
    }
  }

  .membership--renewal {
    font-size: $font-sm;
    margin-top: 10px;
    text-align: center;
    color: currentColor;

    .btn--whatsapp {
      height: 36px;
      border-radius: 20px;
      padding: 0 16px;
    }
  }
}
</style>
